import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  useMediaQuery,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import axiosInstance from "../../utils/axiosInstance";
import CustomerForm from "../customer/CustomerForm";
import { format } from "date-fns";
import { DATE_FORMAT_AM_PM } from "../../utils/constants";

const CustomerDataTable = ({ apiUrl }) => {
  const [rows, setRows] = useState([]); // Customer data
  const [loading, setLoading] = useState(false); // Loading state
  const [totalRows, setTotalRows] = useState(0); // Total rows count
  const [searchText, setSearchText] = useState(""); // Search text
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // DataGrid uses 0-based indexing
    pageSize: 10, // Default rows per page
  });  const [formOpen, setFormOpen] = useState(false); // Modal state for Add Customer
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  }); // Snackbar for notifications
  const [selectedCustomer, setSelectedCustomer] = useState(null); // Define selectedCustomer
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null); // Anchor element for the menu
  const [menuRow, setMenuRow] = useState(null); // Row associated with the menu
  const [sortModel, setSortModel] = useState([
    { field: "modifiedDate", sort: "desc" },
  ]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(apiUrl, {
        params: {
          page: paginationModel.page + 1, // Convert 0-based to 1-based indexing for backend
          limit: paginationModel.pageSize,
          sortField: sortModel[0]?.field || "modifiedDate",
          sortOrder: sortModel[0]?.sort || "desc",
        },
      });

      // Map rows to include a sample 'status' field if not present
      const customersWithStatus = response.data.customers.map((customer) => ({
        ...customer,
        status: customer.status || "PENDING", // Replace with actual status field
        createdDate: customer.createdDate || "1970-01-01T00:00:00.000Z", // Fallback value
        modifiedDate: customer.modifiedDate || "1970-01-01T00:00:00.000Z", // Fallback value
      }));

      setRows(customersWithStatus);
      setTotalRows(response.data.pagination.total); // Update total rows count
    } catch (error) {
      console.error("Error fetching customers:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch customers.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [apiUrl, paginationModel, searchText, sortModel]);

  const handleSortModelChange = (model) => {
    setSortModel(model); // Update sort model
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchorEl(event.currentTarget); // Set the anchor element
    setMenuRow(row); // Set the associated row
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null); // Clear the anchor element
    setMenuRow(null); // Clear the associated row
  };

  const handleOpenDeleteDialog = (customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  // Delete a customer
  const handleConfirmDelete = async () => {
    try {
      if (customerToDelete) {
        await axiosInstance.delete(`${apiUrl}/${customerToDelete.customerId}`);
        setSnackbar({
          open: true,
          message: "Customer deleted successfully!",
          severity: "success",
        });
        fetchCustomers(); // Refresh data
      }
    } catch (error) {
      console.error("Error deleting customer:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete customer.",
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false); // Close dialog
      setCustomerToDelete(null); // Reset selected customer
    }
  };

  // Open Add Form
  const handleOpenAddForm = () => {
    setSelectedCustomer(null); // Clear selectedCustomer for adding
    setFormOpen(true);
  };

  // Open Edit Form
  const handleOpenEditForm = (customer) => {
    setSelectedCustomer(customer); // Set the selected customer for editing
    setFormOpen(true);
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchText(event.target.value);
  };

  // Close Snackbar
  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return; // Ignore clickaway events
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Define columns for DataGrid with responsive settings
  const columns = [
    { 
      field: "customerId", 
      headerName: "Customer ID", 
      flex: isMobile ? 0.8 : 0.5,
      minWidth: isMobile ? 80 : 100,
    },
    { 
      field: "name", 
      headerName: "Name", 
      flex: 1,
      minWidth: isMobile ? 100 : 120,
    },
    { 
      field: "email", 
      headerName: "Email", 
      flex: isMobile ? 1.2 : 1.5,
      minWidth: isMobile ? 120 : 150,
    },
    { 
      field: "phone", 
      headerName: "Phone", 
      flex: 1,
      minWidth: isMobile ? 100 : 120,
      hide: isMobile, // Hide on mobile to save space
    },
    { 
      field: "userName", 
      headerName: "Last Modified By", 
      flex: 1,
      minWidth: isMobile ? 100 : 120,
      hide: isMobile, // Hide on mobile to save space
    },
    {
      field: "createdDate",
      headerName: "Created Date",
      width: isMobile ? 120 : 200,
      hide: isMobile, // Hide on mobile to save space
      valueFormatter: (date) => {
        if (!date) return "";
        try {
          return format(new Date(date), DATE_FORMAT_AM_PM);
        } catch (error) {
          console.error("Invalid date format:", date);
          return "Invalid Date";
        }
      },
    },
    {
      field: "modifiedDate",
      headerName: "Modified Date",
      width: isMobile ? 120 : 200,
      valueFormatter: (date) => {
        if (!date) return "";
        try {
          return format(new Date(date), DATE_FORMAT_AM_PM);
        } catch (error) {
          console.error("Invalid date format:", date);
          return "Invalid Date";
        }
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: isMobile ? 80 : 100,
      renderCell: (params) => {
        const statusColors = {
          Active: "success",
          Inactive: "default",
        };

        return (
          <Chip
            label={params.row.status || "Unknown"}
            color={statusColors[params.row.status] || "default"}
            size="small"
            sx={{
              fontWeight: "bold",
              textTransform: "capitalize",
              borderRadius: 1,
              padding: "0 8px",
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="more"
          aria-controls="actions-menu"
          aria-haspopup="true"
          onClick={(event) => handleMenuOpen(event, params.row)}
          size="small"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", padding: { xs: 1, sm: 2 } }}>
      {/* Table Title and Search */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          marginBottom: 2,
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography 
          variant={isMobile ? "h5" : "h4"}
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          Customers
        </Typography>
        
        <Box sx={{ 
          display: "flex", 
          gap: { xs: 1, sm: 2 },
          flexDirection: { xs: "column", sm: "row" },
          width: { xs: "100%", sm: "auto" },
        }}>
          <TextField
            label="Search"
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            value={searchText}
            onChange={handleSearch}
            placeholder="Search by name"
            sx={{ 
              width: { xs: "100%", sm: "250px" },
              '& .MuiOutlinedInput-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAddForm}
            size={isMobile ? "small" : "medium"}
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
          >
            {isMobile ? "Add" : "Add Customer"}
          </Button>
        </Box>
      </Box>

      {/* DataGrid */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: { xs: 250, sm: 300 },
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            height: { xs: '60vh', sm: '70vh' },
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
            width: '100%',
            overflow: "hidden",
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            disableSelectionOnClick
            pagination
            getRowId={(row) => row.customerId}
            pageSizeOptions={isMobile ? [5, 10] : [10, 25, 50, 100]}
            rowsPerPageOptions={isMobile ? [5, 10] : [10, 25, 50, 100]}
            paginationModel={paginationModel} 
            onPaginationModelChange={(newModel) => {
              console.log("Pagination model changed:", newModel);
              setPaginationModel(newModel);
            }}
            rowCount={totalRows}
            onSortModelChange={handleSortModelChange}
            paginationMode="server"
            sortModel={sortModel}
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f4f6f8",
                fontWeight: "bold",
                fontSize: { xs: "12px", sm: "14px" },
              },
              "& .MuiDataGrid-cell": {
                fontSize: { xs: "11px", sm: "13px" },
              },
              "& .MuiDataGrid-footerContainer": {
                fontSize: { xs: "12px", sm: "14px" },
              },
            }}
            components={{
              NoRowsOverlay: () => (
                <Box sx={{ textAlign: "center", padding: 2 }}>
                  <p>No customers found</p>
                </Box>
              ),
            }}
          />

          {/* Actions Menu */}
          <Menu
            id="actions-menu"
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() => {
                handleOpenEditForm(menuRow);
                handleMenuClose();
              }}
            >
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleOpenDeleteDialog(menuRow);
                handleMenuClose();
              }}
            >
              Delete
            </MenuItem>
          </Menu>
        </Box>
      )}

      {/* Add Customer Form */}
      <CustomerForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initialValues={selectedCustomer || {}}
        isUpdate={!!selectedCustomer}
        refreshCustomers={fetchCustomers}
        setSnackbar={setSnackbar} // Pass the setSnackbar function from the parent
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete{" "}
          <strong>{customerToDelete?.name} </strong>
          with email <strong>{customerToDelete?.email}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerDataTable;
